import { SESSION_CHANNEL } from '~constants/session';

import type { SignOutReason } from '~main/reducers/login';

export type SessionMessage =
  | { type: 'expiry'; expiresAt: number; absoluteExpiresAt: number; reauthExpiresAt: number }
  | { type: 'signed-out'; reason: SignOutReason }
  | { type: 'foreground'; at: number }
  | { type: 'reauth-required' }
  | { type: 'reauthed'; expiresAt: number; absoluteExpiresAt: number; reauthExpiresAt: number }
  | { type: 'key-changed' };

type Handler = (message: SessionMessage) => void;

const handlers = new Set<Handler>();
let channel: BroadcastChannel | null = null;

// One channel per tab. A BroadcastChannel never delivers to the object that posted, so sharing one
// object means a tab never reacts to its own messages.
const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.onmessage = (event: MessageEvent<SessionMessage>) => {
        if (!event.data || typeof event.data.type !== 'string') return;
        handlers.forEach((handler) => handler(event.data));
      };
    } catch {
      channel = null;
    }
  }
  return channel;
};

export const publishSessionMessage = (message: SessionMessage) => {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // A closed channel or a structured-clone failure must never break the caller.
  }
};

/** Subscribes to messages from other tabs. Returns an unsubscribe function. */
export const subscribeSessionMessages = (handler: Handler): (() => void) => {
  getChannel();
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
};
